import { Fragment } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import 'photoswipe/dist/photoswipe.css';
import CarouselGallery, {
  // eslint-disable-next-line import/named
  captionContent,
} from 'sealink/react-components/carousel-gallery';
import useBootStrapMediaQuery from 'utils/hooks/use_bootstrap_media_query';

const LoadingState = ({ isMobile }) => {
  if (isMobile) {
    return <Skeleton height="15rem" />;
  }

  return (
    <div className="container-xxl grid-gallery">
      <Gallery withCaption>
        <div className="grid-gallery__container--five-images">
          {[...Array(5)].map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="grid-gallery__img w-100" key={`skeleton-${index}`}>
              <Skeleton height="100%" />
            </div>
          ))}
        </div>
      </Gallery>
    </div>
  );
};

const GridGallery = ({ loading, images }) => {
  const { downMd } = useBootStrapMediaQuery();
  // Present loading if loading flag is explicitly set,
  // or images is anything other than an array (e.g. may be undefined or falsey during loading)
  if (loading || !Array.isArray(images)) {
    return <LoadingState isMobile={downMd} />;
  }

  let displayedImages = images;
  let hiddenImages = [];
  let containerClassName = '';
  const imagesCount = images.length;
  if (imagesCount === 1) {
    displayedImages = images;
    containerClassName = 'grid-gallery__container--one-image';
  } else if (imagesCount <= 2) {
    displayedImages = images.slice(0, 2);
    hiddenImages = images.slice(2);
    containerClassName = 'grid-gallery__container--two-images';
  } else if (imagesCount < 5) {
    displayedImages = images.slice(0, 3);
    hiddenImages = images.slice(3);
    containerClassName = 'grid-gallery__container--three-images';
  } else {
    displayedImages = images.slice(0, 5);
    hiddenImages = images.slice(5);
    containerClassName = 'grid-gallery__container--five-images';
  }

  const renderDesktopView = () => (
    <div className="container-xxl">
      <div className="grid-gallery">
        <Gallery withCaption>
          <div className={containerClassName}>
            {displayedImages.map((image, index) => (
              <Fragment key={image.thumbnail}>
                <Item
                  original={image.src}
                  thumbnail={image.thumbnail}
                  width={image.originalWidth}
                  height={image.originalHeight}
                  caption={captionContent(image.caption)}
                >
                  {({ ref, open }) => (
                    <img
                      className="grid-gallery__img"
                      src={index === 0 ? image.src : image.thumbnail}
                      width={image.thumbnailWidth}
                      height={image.thumbnailHeight}
                      alt={image.alt}
                      ref={ref}
                      onClick={open}
                    />
                  )}
                </Item>
              </Fragment>
            ))}

            {hiddenImages.map((image) => (
              <div className="grid-gallery__img--hidden" key={image.thumbnail}>
                <Item
                  original={image.src}
                  thumbnail={image.thumbnail}
                  width={image.originalWidth}
                  height={image.originalHeight}
                  caption={captionContent(image.caption)}
                >
                  {({ ref }) => <div ref={ref} />}
                </Item>
              </div>
            ))}
          </div>
        </Gallery>

        {!isEmpty(hiddenImages) && (
          <div className="grid-gallery__count-badge">
            <i className="far fa-images" />
            {` ${imagesCount} photos`}
          </div>
        )}
      </div>
    </div>
  );

  const renderMobileView = () => CarouselGallery({ images });

  return downMd ? renderMobileView() : renderDesktopView();
};

export default GridGallery;

const stringOrNumber = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
]);

GridGallery.propTypes = {
  loading: PropTypes.bool,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      thumbnail: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
      caption: PropTypes.string,
      originalWidth: stringOrNumber,
      originalHeight: stringOrNumber,
      thumbnailWidth: stringOrNumber,
      thumbnailHeight: stringOrNumber,
    })
  ),
};

GridGallery.defaultProps = {
  loading: false,
};

LoadingState.propTypes = {
  isMobile: PropTypes.bool,
};
